//* Copie e cole este template para criar uma nova fase
<template>
  <div v-if="!!phaseData">
    <v-sheet v-if="loading || error" class="d-flex flex-column align-center justify-center row-gap-2" height="50vh">
      <template v-if="error">
        <h3>Um erro ocorreu ao tentar carregar os dados da tela!</h3>
        <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
          >tentar novamente <v-icon class="ml-2" large>mdi-refresh</v-icon></v-btn
        >
      </template>
      <template v-else>
        <h3>Carregando dados...</h3>
        <v-progress-circular indeterminate />
      </template>
    </v-sheet>
    <div v-else>
      <radio-options
        label="O paciente tem diagnóstico de artrite psoriásica?"
        :options="diagnosisOptions"
        v-model="phaseData['diagnostico_artrite_psoriatica'].value"
        row
        hide-details
        clearable
        :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
        :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
      />

      <field-label
        label="PEST"
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
        class="mt-4"
        v-if="showPestCaspar"
      >
        <radio-options
          v-for="option in pestOptions"
          :key="option.key"
          clearable
          :label="option.name"
          v-model="phaseData.pest[option.key].value"
          row
          :options="defaultYesNoOptions"
          :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
          :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
        />
      </field-label>

      <field-label
        label="CASPAR"
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
        class="mt-4"
        v-if="showPestCaspar && !attendance.data.summarizedFlow"
      >
        <div>
          <span class="">Exame complementar que aumentará ainda mais a precisão do diagnóstico.</span>
        </div>
        <div class="mt-4">
          <radio-options
            v-for="option in casparOptions"
            v-show="option.key !== 'caspar_condicao_psoriase' || phaseData.caspar['caspar_psoriase'].value"
            :key="option.key"
            clearable
            :label="option.name"
            v-model="phaseData.caspar[option.key].value"
            row
            :options="option.options || defaultYesNoOptions"
            :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
            :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
            @input="checkCaspar(option.key, $event)"
          />
        </div>
      </field-label>
      <field-label class="mt-8" label="Anotações">
        <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
      </field-label>
    </div>
  </div>
</template>
<script>
import BasePhase from '../../general/phase/BasePhase.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import FieldLabel from '@/components/FieldLabel.vue'

import { createNamespacedHelpers } from 'vuex'
import TextArea from '@/components/TextArea.vue'
const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

export default {
  components: { RadioOptions, FieldLabel, TextArea },
  extends: BasePhase,
  computed: {
    ...attendanceGettersMap(['attendance', 'attendanceDiseaseId']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    showPestCaspar() {
      if (!this.phaseData) {
        return false
      }

      return this.phaseData['diagnostico_artrite_psoriatica']?.value === 'NAO_SEI'
    },
    defaultYesNoOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    diagnosisOptions() {
      return [
        { text: 'Sim', value: 'SIM' },
        { text: 'Não sei', value: 'NAO_SEI' },
        { text: 'Não', value: 'NAO' },
      ]
    },
    casparOptions() {
      return [
        { key: 'caspar_psoriase', name: 'Psoríase' },
        {
          key: 'caspar_condicao_psoriase',
          name: 'Indique a condição da psoríase',
          options: [
            { text: 'Atual', value: 'ATUAL' },
            { text: 'Prévia', value: 'PREVIA' },
            { text: 'História familiar', value: 'HISTORIA_FAMILIAR' },
          ],
        },
        { key: 'caspar_distrofia_ungueal', name: 'Distrofia ungueal' },
        { key: 'caspar_fator_reumatoide_negativo', name: 'Fator reumatoide negativo' },
        { key: 'caspar_dactilite', name: 'Histórico ou presença de dactilite' },
        { key: 'caspar_evidencia_neoformacao_ossea', name: 'Rx com evidência de neoformação óssea' },
      ]
    },
    pestOptions() {
      return [
        { key: 'pest_articulacao_edemaciada', name: 'Já teve alguma articulação edemaciada?' },
        { key: 'pest_artrite_previa_diagnosticada', name: 'Algum médico já disse que tem artrite?' },
        { key: 'pest_orificios_unhas', name: 'Tem orifícios, furinhos nas unhas da mãos ou pés?' },
        { key: 'pest_dor_calcanhar', name: 'Já teve dor no calcanhar?' },
        {
          key: 'pest_dedo_salsicha',
          name: 'Já teve um dedo edemacionado ou dolorido sem motivo aparente? (Dedo em salsicha)',
        },
      ]
    },
  },
  data() {
    return {
      loading: false,
      error: false,
    }
  },
  methods: {
    buildEmptyCaspar() {
      return this.casparOptions.reduce((acc, option) => ({ ...acc, [option.key]: { value: null } }), {})
    },
    buildEmptyPest() {
      return this.pestOptions.reduce((acc, option) => ({ ...acc, [option.key]: { value: null } }), {})
    },
    checkCaspar(key, value) {
      if (key === 'caspar_psoriase') {
        this.phaseData.caspar['caspar_condicao_psoriase'] = { value: null }
      }
    },
    getDefaultData() {
      const data = {
        caspar: { ...this.buildEmptyCaspar() },
        pest: { ...this.buildEmptyPest() },
        diagnostico_artrite_psoriatica: { value: null },
        notes: null,
      }
      return data
    },
  },
  watch: {
    showPestCaspar(newVal) {
      if (!newVal) {
        this.phaseData.pest = { ...this.buildEmptyPest() }
        this.phaseData.caspar = { ...this.buildEmptyCaspar() }
      }
    },
  },
}
</script>

<style scoped></style>
